/**
 * Kriteret e një pozite — një grusht fushash, jo një motor kompetencash.
 *
 * Çdo fushë këtu duhet të paguajë veten: ajo ekziston vetëm sepse ndryshon
 * vendimin «a ia vlen ta kontaktojmë këtë person». Gjuha me nivel, vitet e
 * përvojës, angazhimi, vendi i punës dhe lokacioni janë pikërisht ato që HR-ja
 * do t'i pyeste në telefon; çdo fushë tjetër do të ishte punë shtesë për të
 * plotësuar dhe asnjë përgjigje më shumë.
 *
 * Ndarja «e detyrueshme / e preferuar» ka kuptim praktik: e para thotë pse dikush
 * nuk kualifikohet, e dyta thotë pse dikush shquhet. Asnjëra nuk refuzon askënd
 * vetvetiu — sistemi krahason, njeriu vendos.
 */
import React from 'react';
import {
  Box, Chip, Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { LANGUAGES, LANGUAGE_LEVELS, EMPLOYMENT_TYPES, WORKPLACE_TYPES } from '../../config/recruitmentCriteria';

const asList = (v) => String(v || '').split(',').map((x) => x.trim()).filter(Boolean);

function Side({ title, hint, value = {}, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const lang = (value.languages || [])[0] || { code: '', level: '' };

  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
      <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
        <Grid item xs={7} sm={5}>
          <TextField select size="small" fullWidth label="Gjuha" value={lang.code || ''}
            onChange={(e) => {
              const code = e.target.value;
              set({ languages: code ? [{ code, level: lang.level || 'B2' }] : [] });
            }}>
            <MenuItem value="">—</MenuItem>
            {LANGUAGES.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={5} sm={3}>
          <TextField select size="small" fullWidth label="Niveli" value={lang.level || ''} disabled={!lang.code}
            onChange={(e) => set({ languages: [{ code: lang.code, level: e.target.value }] })}>
            {LANGUAGE_LEVELS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField size="small" fullWidth type="number" label="Përvoja (vjet)"
            inputProps={{ min: 0, max: 40 }}
            value={value.minExperienceYears ?? ''}
            onChange={(e) => set({ minExperienceYears: e.target.value === '' ? undefined : Number(e.target.value) })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select size="small" fullWidth label="Angazhimi" value={(value.employmentTypes || [])[0] || ''}
            onChange={(e) => set({ employmentTypes: e.target.value ? [e.target.value] : [] })}>
            <MenuItem value="">—</MenuItem>
            {EMPLOYMENT_TYPES.map((t) => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select size="small" fullWidth label="Vendi i punës" value={(value.workplaceTypes || [])[0] || ''}
            onChange={(e) => set({ workplaceTypes: e.target.value ? [e.target.value] : [] })}>
            <MenuItem value="">—</MenuItem>
            {WORKPLACE_TYPES.map((t) => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label="Lokacioni" placeholder="prishtinë, ferizaj"
            helperText="Të ndara me presje"
            value={(value.locations || []).join(', ')}
            onChange={(e) => set({ locations: asList(e.target.value) })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label="Aftësi" placeholder="crm, excel"
            helperText="Të ndara me presje"
            value={(value.skills || []).join(', ')}
            onChange={(e) => set({ skills: asList(e.target.value) })} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default function PositionCriteriaFields({ value = {}, onChange }) {
  const set = (side) => (v) => onChange({ ...value, [side]: v });
  const count = (o = {}) => Object.values(o).filter((x) => (Array.isArray(x) ? x.length : x !== undefined && x !== '')).length;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Kriteret e pozitës</Typography>
        <Chip size="small" label={`${count(value.required) + count(value.preferred)} të përcaktuara`}
          sx={{ height: 19, fontSize: '.64rem' }} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Përdoren për të gjetur kandidatë ekzistues kur hapet kjo pozitë. Lënia bosh nuk prish asgjë —
        vetëm e bën kërkimin historik pa arsye për të treguar.
      </Typography>
      <Side title="Të detyrueshme" hint="Pa këto, personi ka gjasa të mos kualifikohet."
        value={value.required || {}} onChange={set('required')} />
      <Side title="Të preferuara" hint="Këto e bëjnë personin të shquhet, por nuk e përjashtojnë."
        value={value.preferred || {}} onChange={set('preferred')} />
    </Stack>
  );
}
